import { css } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import { match } from "ts-pattern";
import type { PickRequired } from "../types";
import type { FilledTabsProps, TabsProps, TextTabsProps } from "./Tabs.types";

const filledCss = ({ size }: PickRequired<FilledTabsProps, "size">) =>
  css({
    gap: vars.spacing[size === "large" ? "15" : 1],

    '[role="tab"]': {
      ...vars.semantic.typography[`label-${size === "large" ? "large" : "medium"}-bold`],

      padding: match(size)
        .with("small", () => `${vars.spacing[1]} ${vars.spacing[3]}`)
        .with("medium", () => `${vars.spacing[2]} ${vars.spacing[3]}`)
        .with("large", () => `${vars.spacing[3]} ${vars.spacing[4]}`)
        .exhaustive(),

      gap: vars.spacing[size === "medium" || size === "small" ? 1 : "15"],

      transition: "background-color 0.3s ease-out",

      '&:not(:disabled)[data-selected="true"]': {
        backgroundColor: vars.semantic.color.actionPrimary,
        color: vars.semantic.color.textInverse,

        svg: {
          color: vars.semantic.color.iconInverse,
        },
      },

      '&:not(:disabled, [data-selected="true"]):active': {
        backgroundColor: vars.semantic.color.actionSecondaryPressed,
      },

      '&:not([data-selected="true"], :active, :disabled):hover': {
        backgroundColor: vars.semantic.color.actionSecondaryHover,
        '& [data-clay-badge-on="false"]': {
          backgroundColor: vars.semantic.color.actionPrimaryDisabled,
        },
      },

      "&:disabled": {
        backgroundColor: "transparent",
        color: vars.semantic.color.textDisabled,

        svg: {
          color: vars.semantic.color.iconDisabled,
        },
      },

      borderRadius: vars.rounded[size === "small" ? "small" : "medium"],
    },
  });

const textCss = ({ size }: PickRequired<TextTabsProps, "size">) =>
  css({
    ...match(size)
      .with("large", () => ({
        gap: vars.spacing[4],
        ...vars.semantic.typography["label-large"],
      }))
      .with("medium", () => ({
        gap: vars.spacing[3],
        ...vars.semantic.typography["label-medium"],
      }))
      .with("xlarge", () => ({
        gap: vars.spacing[5],
        ...vars.semantic.typography["label-xlarge-bold"],
      }))
      .with("2xlarge", () => ({
        gap: vars.spacing[6],
        ...vars.semantic.typography["label-3xlarge-bold"],
      }))
      .exhaustive(),

    '[role="tab"]': {
      padding: `${vars.spacing[size === "medium" ? "2" : "3"]} 0`,
      gap: vars.spacing[size === "medium" ? 1 : "15"],

      transition: "color 0.3s ease-out",
      color: vars.semantic.color.textSub,

      svg: {
        color: vars.semantic.color.iconSub,
      },

      '&:not(:disabled)[data-selected="true"]': {
        color: vars.semantic.color.text,

        svg: {
          color: vars.semantic.color.icon,
        },
      },

      '&:not([data-selected="true"], :active, :disabled):hover': {
        color: vars.semantic.color.text,

        svg: {
          color: vars.semantic.color.icon,
        },
      },

      "&:disabled": {
        color: vars.semantic.color.textDisabled,
        cursor: "not-allowed",

        svg: {
          color: vars.semantic.color.iconDisabled,
        },
      },
    },
  });

export const tabListCSS = (props: Pick<TabsProps, "variant" | "size" | "fullWidth">) => {
  const variantStyle = match(props)
    .with({ variant: "filled" }, filledCss)
    .with({ variant: "text" }, textCss)
    .exhaustive();

  return css({
    '[role="tab"]': {
      flex: props.fullWidth ? 1 : "auto",
    },

    ...variantStyle,
  });
};
